import { Component, inject, OnInit, signal, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { EmployeeService } from '../../services/employee.service';
import { Employee } from '../../shared/models/employee.model';
import { SlButtonComponent } from '../../shared/wrapper-components/sl-button/sl-button.component';
import { SlAvatarComponent } from '../../shared/wrapper-components/sl-avatar/sl-avatar.component';

@Component({
  selector: 'app-employee-profile',
  standalone: true,
  imports: [
    NgFor, 
    NgIf, 
    NgClass,
    SlButtonComponent,
    SlAvatarComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './employee-profile.component.html',
  styleUrls: ['./employee-profile.component.css']
})
export class EmployeeProfileComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly employeeService = inject(EmployeeService);

  employee = signal<Employee | null>(null);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  activeTab = signal<'job' | 'personal' | 'emergency'>('job');

  ngOnInit(): void {
    const employeeId = this.route.snapshot.paramMap.get('id');
    if (employeeId) {
      this.loadEmployee(employeeId);
    }
  }

  loadEmployee(id: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.employeeService.getEmployeeById(id).subscribe({
      next: (employee) => {
        if (employee) {
          this.employee.set(employee);
        } else {
          this.error.set('Employee not found');
        }
        this.loading.set(false);
      },
      error: (error) => {
        this.error.set('Failed to load employee details');
        this.loading.set(false);
        console.error('Error loading employee:', error);
      }
    });
  }

  setActiveTab(tab: 'job' | 'personal' | 'emergency'): void {
    this.activeTab.set(tab);
  }

  editEmployee(): void {
    const emp = this.employee();
    if (emp) {
      this.router.navigate(['/employees', emp.id, 'edit']);
    }
  }

  goBack(): void {
    this.router.navigate(['/employees']);
  }

  getEmployeeFullName(): string {
    const emp = this.employee();
    if (!emp) return '';
    return `${emp.personalDetails.firstName} ${emp.personalDetails.lastName}`;
  }

  getEmployeeInitials(): string {
    const emp = this.employee();
    if (!emp) return '';
    return `${emp.personalDetails.firstName.charAt(0)}${emp.personalDetails.lastName.charAt(0)}`.toUpperCase();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }

  formatSalary(salary?: number, currency?: string, frequency?: string): string {
    if (!salary) return 'Not specified';
    const formattedAmount = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP'
    }).format(salary);
    return `${formattedAmount} ${frequency || 'Annual'}`;
  }

  formatWorkingDays(days?: string[]): string {
    if (!days || days.length === 0) return 'Not specified';
    return days.join(', ');
  }

  formatAddress(address: any): string {
    if (!address) return 'Not specified';
    const parts = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.county,
      address.postcode,
      address.country
    ].filter(part => part && part.trim());
    return parts.join(', ');
  }
}
